import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Film, ArrowRight, Play, Pause,
  ChevronRight, Trophy,
  CheckCircle2, XCircle, Loader2, Lock, Image as ImageIcon,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import StudentVideoPlayer from '../../components/SimpleVideoPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==================== Image Viewer with Countdown ====================
const ImageViewer = ({ src, displayDuration = 10, onClose }) => {
  const [timeLeft, setTimeLeft] = useState(displayDuration);
  const [paused, setPaused] = useState(false);
  const [canClose, setCanClose] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const elapsedRef = useRef(0);

  useEffect(() => {
    // After displayDuration seconds from start, allow ESC to close
    const allowCloseTimer = setTimeout(() => setCanClose(true), displayDuration * 1000);
    return () => clearTimeout(allowCloseTimer);
  }, [displayDuration]);

  useEffect(() => {
    if (!paused) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        elapsedRef.current += 100;
        const remaining = Math.max(0, (displayDuration * 1000 - elapsedRef.current) / 1000);
        setTimeLeft(Math.ceil(remaining));
        if (remaining <= 0) {
          clearInterval(timerRef.current);
          onClose();
        }
      }, 100);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [paused, displayDuration, onClose]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setPaused(prev => !prev);
      }
      if (e.code === 'Escape' && canClose) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canClose, onClose]);

  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const progress = timeLeft / displayDuration;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
      <img
        src={src}
        alt="Question media"
        className="max-w-full max-h-full object-contain"
        style={{ maxHeight: '85vh', maxWidth: '90vw' }}
      />
      {/* Countdown overlay */}
      <div className="absolute top-4 right-4 flex flex-col items-center gap-2">
        <div className="relative w-14 h-14">
          <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
            <circle cx="28" cy="28" r={radius} fill="rgba(0,0,0,0.5)" stroke="rgba(255,255,255,0.2)" strokeWidth="3" />
            <circle
              cx="28" cy="28" r={radius}
              fill="none"
              stroke={paused ? '#f59e0b' : '#34c38f'}
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 0.1s linear' }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-white font-bold text-sm">
            {timeLeft}s
          </span>
        </div>
        <div className="text-white/60 text-xs text-center">
          {paused ? '⏸ Paused' : '▶ Running'}
        </div>
      </div>
      {/* Controls hint */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-white/50 text-xs">
          Space = {paused ? 'Resume' : 'Pause'} timer
          {canClose ? ' · ESC = Close' : ` · ESC available after ${timeLeft}s`}
        </p>
      </div>
      {/* Space to toggle pause button */}
      <button
        onClick={() => setPaused(p => !p)}
        className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-black/70 transition-all"
      >
        {paused ? <Play className="h-4 w-4 inline mr-1" /> : <Pause className="h-4 w-4 inline mr-1" />}
        {paused ? 'Resume' : 'Pause'}
      </button>
      {canClose && (
        <button
          onClick={onClose}
          className="absolute top-4 left-24 bg-black/50 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-red-500/70 transition-all"
        >
          Close
        </button>
      )}
    </div>
  );
};

// ==================== Main Student VMCQ Page ====================
const StudentVMCQPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTest, setSelectedTest] = useState(null);
  const [phase, setPhase] = useState('list'); // list | media | reveal | quiz | result
  const [questions, setQuestions] = useState([]);
  const [mediaGroups, setMediaGroups] = useState([]); // [{media_path, media_type, display_duration, question_count}]
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [unlockedUpTo, setUnlockedUpTo] = useState(0); // number of questions unlocked
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [isReplayMode, setIsReplayMode] = useState(false); // Track if user is replaying media
  const [replayMediaIndex, setReplayMediaIndex] = useState(0); // Track which media to replay

  // Helper function to get the media index for a given question index
  const getMediaIndexForQuestion = useCallback((questionIndex) => {
    let questionCount = 0;
    for (let i = 0; i < mediaGroups.length; i++) {
      questionCount += mediaGroups[i]?.question_count || 0;
      if (questionIndex < questionCount) {
        return i;
      }
    }
    return mediaGroups.length - 1; // Default to last media if not found
  }, [mediaGroups]);

  // Get the media index for the current question being viewed
  const currentQuestionMediaIndex = useMemo(() => {
    return getMediaIndexForQuestion(currentIndex);
  }, [currentIndex, getMediaIndexForQuestion]);

  useEffect(() => { fetchTests(); }, []);

  const fetchTests = async () => {
    try {
      const res = await api.get('/tests?module_type=vmcq');
      console.log('[StudentVMCQPage] Fetched tests:', res.data);
      console.log('[StudentVMCQPage] Number of tests:', res.data.length);
      setTests(res.data);
    } catch (err) {
      console.error('[StudentVMCQPage] Error fetching tests:', err);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const startTest = async (test) => {
    try {
      const res = await api.get(`/tests/${test.id}`);
      const testData = res.data;

      // Parse questions
      const processedQs = testData.questions.map(q => {
        const options = Array.isArray(q.options) ? q.options :
          (typeof q.options === 'string' ? JSON.parse(q.options) : []);
        const correctAnswer = options[q.correct_option];
        const shuffled = [...options].sort(() => Math.random() - 0.5);
        return { ...q, options: shuffled, _correctAnswer: correctAnswer };
      });

      // Parse media groups
      let groups = [];
      if (testData.media_groups) {
        try {
          groups = JSON.parse(testData.media_groups);
        } catch {
          groups = [];
        }
      }
      // Fallback: single video in one group covering all questions
      if (groups.length === 0 && testData.video_path) {
        groups = [{
          media_path: testData.video_path,
          media_type: 'video',
          media_filename: testData.video_filename,
          display_duration: 10,
          question_count: processedQs.length
        }];
      }

      setSelectedTest(testData);
      setQuestions(processedQs);
      setMediaGroups(groups);
      setAnswers({});
      setResult(null);
      setCurrentMediaIndex(0);
      setUnlockedUpTo(0);
      setCurrentIndex(0);
      setIsReplayMode(false);
      setReplayMediaIndex(0);
      setPhase('media');
    } catch (err) {
      toast.error('Failed to load test');
    }
  };

  // Called when current media finishes
  const handleMediaEnd = useCallback(() => {
    // If this is a replay, don't unlock new questions - just go back to quiz
    if (isReplayMode) {
      setIsReplayMode(false);
      setPhase('quiz');
      return;
    }
    
    const group = mediaGroups[currentMediaIndex];
    if (!group) return;
    const newUnlocked = Math.min(
      unlockedUpTo + (group.question_count || 0),
      questions.length
    );
    setUnlockedUpTo(newUnlocked);
    setCurrentIndex(unlockedUpTo); // go to first newly unlocked question
    setPhase('reveal');
    setTimeout(() => setPhase('quiz'), 1400);
  }, [mediaGroups, currentMediaIndex, unlockedUpTo, questions.length, isReplayMode]);

  const handleAnswer = (questionId, optionIdx) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionIdx }));
  };

  const handleSubmit = async () => {
    const unanswered = questions.slice(0, unlockedUpTo).filter(q => answers[q.id] === undefined);
    if (unanswered.length > 0) {
      toast.error('Please answer all unlocked questions before submitting');
      return;
    }
    setSubmitting(true);
    try {
      const formattedAnswers = questions.slice(0, unlockedUpTo).map(q => ({
        question_id: q.id,
        answer: q.options[answers[q.id]] ?? ''
      }));
      const res = await api.post('/quiz/submit', formattedAnswers);
      setResult(res.data);
      setPhase('result');
    } catch {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    setPhase('list');
    setSelectedTest(null);
    setQuestions([]);
    setMediaGroups([]);
    setAnswers({});
    setResult(null);
    fetchTests();
  };

  // ==================== RENDER PHASES ====================

  if (phase === 'list') {
    return (
      <DashboardLayout>
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Film className="h-7 w-7 text-sky-600" />
              Cinematic MCQ Quiz
            </h1>
            <p className="text-slate-500 dark:text-slate-400">Watch video/image, then answer comprehension questions</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>
          ) : tests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Film className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No cinematic quizzes available yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tests.map(test => {
                const isLocked = !!test.is_locked;
                return (
                  <Card
                    key={test.id}
                    className={`group transition-all ${isLocked ? 'opacity-75' : 'cursor-pointer hover:shadow-lg'}`}
                    onClick={() => !isLocked && startTest(test)}
                    data-testid={`test-card-${test.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">
                          {test.test_number}
                        </span>
                        {isLocked && (
                          <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                            <Lock className="h-3 w-3" /> Locked
                          </span>
                        )}
                      </div>
                      <div className="inline-flex p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 mb-3 group-hover:scale-110 transition-transform">
                        <Film className="h-6 w-6 text-sky-600 dark:text-sky-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{test.test_name}</h3>
                      <p className="text-xs text-slate-500 mb-3">{test.question_count || 0} questions · Cinematic quiz</p>
                      {isLocked ? (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                          This test is locked. You cannot attempt it until it's unlocked.
                        </p>
                      ) : (
                        <div className="flex items-center justify-between text-sm mt-2">
                          <span className="text-slate-500">Attempts: {test.attempts || 0}</span>
                          <span className={`font-semibold ${(test.best_score || 0) >= 80 ? 'text-emerald-600' : (test.best_score || 0) >= 50 ? 'text-amber-600' : 'text-slate-600'}`}>
                            Best: {test.best_score || 0}%
                          </span>
                        </div>
                      )}
                      {!isLocked && (
                        <div className="mt-4 flex items-center text-sky-600 dark:text-sky-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                          Watch & Answer <ArrowRight className="h-4 w-4 ml-1" />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </DashboardLayout>
    );
  }

  if (phase === 'media') {
    // Use replayMediaIndex when in replay mode, otherwise use currentMediaIndex
    const mediaIndexToShow = isReplayMode ? replayMediaIndex : currentMediaIndex;
    const group = mediaGroups[mediaIndexToShow];
    if (!group) return null;
    const mediaUrl = `${BACKEND_URL}${group.media_path}`;
    const isImage = group.media_type === 'image';
    const totalGroups = mediaGroups.length;

    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-4xl mx-auto animate-fade-in">
          <div>
            <button onClick={goBack} className="text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white mb-2 flex items-center gap-1">
              ← Back to tests
            </button>
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Film className="h-5 w-5 text-sky-600" />
                {selectedTest?.test_name}
              </h1>
              <span className="text-sm text-slate-500">
                {isReplayMode ? `Replaying Media ${mediaIndexToShow + 1}` : `Media ${mediaIndexToShow + 1} of ${totalGroups}`}
              </span>
            </div>
            <p className="text-slate-500 text-sm mt-1">
              {isImage
                ? `View the image for ${group.display_duration || 10}s, then answer ${group.question_count} question${group.question_count !== 1 ? 's' : ''}`
                : `Watch the video, then answer ${group.question_count} question${group.question_count !== 1 ? 's' : ''}`
              }
            </p>
          </div>

          {/* Progress: media groups */}
          <div className="flex gap-1.5">
            {mediaGroups.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full transition-all ${
                i < currentMediaIndex ? 'bg-emerald-500' :
                i === mediaIndexToShow ? 'bg-purple-500' :
                'bg-slate-200 dark:bg-slate-700'
              }`} />
            ))}
          </div>

          {isImage ? (
            <div className="bg-black rounded-2xl overflow-hidden shadow-2xl border border-purple-500/20">
              <div className="p-3 bg-gradient-to-r from-purple-900/50 to-indigo-900/50">
                <p className="text-white/70 text-xs text-center font-medium">
                  Click the button below to view the image for {group.display_duration || 10} seconds
                </p>
              </div>
              <div className="aspect-video flex items-center justify-center bg-slate-900">
                <div className="text-center">
                  <ImageIcon className="h-16 w-16 text-purple-400/50 mx-auto mb-4" />
                  <Button
                    onClick={() => setShowImageViewer(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold"
                    data-testid="view-image-btn"
                  >
                    <Play className="h-5 w-5 mr-2" fill="white" />
                    View Image
                  </Button>
                  <p className="text-white/40 text-xs mt-3">Space = Pause/Resume timer · ESC = Close (after {group.display_duration || 10}s)</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-black rounded-2xl overflow-hidden shadow-2xl border border-purple-500/20">
              <div className="p-3 bg-gradient-to-r from-purple-900/50 to-indigo-900/50">
                <p className="text-white/70 text-xs text-center font-medium">
                  Click to watch the complete video to unlock questions
                </p>
              </div>
              <div className="aspect-video flex items-center justify-center bg-slate-900">
                <div className="text-center">
                  <Film className="h-16 w-16 text-purple-400/50 mx-auto mb-4" />
                  <Button
                    onClick={() => setShowVideoPlayer(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold"
                    data-testid="play-video-btn"
                  >
                    <Play className="h-5 w-5 mr-2" fill="white" />
                    Play Video
                  </Button>
                  <p className="text-white/40 text-xs mt-3">Video will play in fullscreen</p>
                </div>
              </div>
            </div>
          )}

          {/* Show already unlocked questions summary */}
          {unlockedUpTo > 0 && (
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 text-sm text-emerald-700 dark:text-emerald-400">
              {unlockedUpTo} question{unlockedUpTo !== 1 ? 's' : ''} unlocked · 
              <button onClick={() => { setCurrentIndex(0); setPhase('quiz'); }} className="ml-1 underline font-medium">
                Go to questions
              </button>
            </div>
          )}
        </div>

        {/* Image Viewer */}
        {showImageViewer && (
          <ImageViewer
            src={`${BACKEND_URL}${group.media_path}`}
            displayDuration={group.display_duration || 10}
            onClose={() => {
              setShowImageViewer(false);
              handleMediaEnd();
            }}
          />
        )}

        {/* Video Player - full viewport overlay */}
        {showVideoPlayer && (
          <StudentVideoPlayer
            videoSrc={group.media_path}
            title={selectedTest?.test_name || 'Cinematic Quiz'}
            subtitle={isReplayMode ? `Replaying Video ${mediaIndexToShow + 1}` : `Video ${mediaIndexToShow + 1} of ${mediaGroups.length}`}
            allowSeek={false}
            autoPlay={true}
            onVideoEnd={() => {
              setShowVideoPlayer(false);
              handleMediaEnd();
            }}
            onClose={() => {
              setShowVideoPlayer(false);
            }}
          />
        )}
      </DashboardLayout>
    );
  }

  if (phase === 'reveal') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center animate-fade-in">
            <Film className="h-24 w-24 mx-auto text-purple-500 mb-4 animate-pulse" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Unveiling Questions...</h2>
            <p className="text-slate-500">Get ready to answer!</p>
            <div className="mt-6 flex justify-center gap-2">
              {[0,1,2].map(i => (
                <div key={i} className="w-3 h-3 rounded-full bg-purple-500 animate-bounce" style={{animationDelay:`${i*0.2}s`}} />
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (phase === 'quiz') {
    const unlockedQuestions = questions.slice(0, unlockedUpTo);
    const question = unlockedQuestions[currentIndex];
    if (!question) return null;
    
    const allUnlockedAnswered = unlockedQuestions.every(q => answers[q.id] !== undefined);
    const isLastGroup = currentMediaIndex >= mediaGroups.length - 1;
    const allQuestionsUnlocked = unlockedUpTo >= questions.length;
    const showSubmit = allQuestionsUnlocked && allUnlockedAnswered;
    const showNextMedia = !allQuestionsUnlocked && allUnlockedAnswered;

    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto space-y-4 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Film className="h-5 w-5 text-sky-600" />
              {selectedTest?.test_name}
            </h2>
            <span className="text-sm text-slate-500">Q{currentIndex + 1} of {unlockedUpTo} unlocked</span>
          </div>

          {/* Progress */}
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div className="bg-purple-600 h-2 rounded-full transition-all"
              style={{ width: `${(unlockedUpTo / questions.length) * 100}%` }} />
          </div>
          <p className="text-xs text-slate-500 text-right">
            {unlockedUpTo}/{questions.length} questions unlocked
          </p>

          {/* Question card */}
          <Card className="shadow-lg border-purple-200 dark:border-purple-800">
            <CardContent className="p-6">
              <p className="text-lg font-semibold text-slate-900 dark:text-white mb-6">{question.question_text}</p>
              <div className="grid grid-cols-1 gap-3">
                {question.options.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(question.id, idx)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all font-medium ${
                      answers[question.id] === idx
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                        : 'border-slate-200 dark:border-slate-700 hover:border-purple-400 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 text-slate-900 dark:text-white'
                    }`}
                  >
                    <span className="font-bold text-sky-600 dark:text-sky-400 mr-2">
                      {String.fromCharCode(65 + idx)}.
                    </span>
                    {opt}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}>
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <div className="flex gap-2">
              {showNextMedia && (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={() => {
                    setCurrentMediaIndex(prev => prev + 1);
                    setPhase('media');
                  }}
                >
                  <Play className="h-4 w-4 mr-1" />
                  Play Next Media
                </Button>
              )}
              {showSubmit && (
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Submit Answers
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setCurrentIndex(Math.min(unlockedQuestions.length - 1, currentIndex + 1))}
              disabled={currentIndex >= unlockedQuestions.length - 1}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {/* Replay media button - shows media for current question */}
          <div className="text-center">
            <button
              onClick={() => {
                // Set replay mode - this prevents unlocking new questions when media ends
                setIsReplayMode(true);
                // Set which media to replay based on current question
                setReplayMediaIndex(currentQuestionMediaIndex);
                setPhase('media');
              }}
              className="text-sm text-sky-600 dark:text-sky-400 hover:underline"
            >
              {mediaGroups[currentQuestionMediaIndex]?.media_type === 'image' ? <ImageIcon className="h-4 w-4 inline mr-1" /> : <Film className="h-4 w-4 inline mr-1" />}
              Replay Media {currentQuestionMediaIndex + 1}
            </button>
          </div>

          {/* Question dots */}
          <div className="flex justify-center flex-wrap gap-1.5">
            {questions.map((q, i) => (
              <button
                key={i}
                onClick={() => i < unlockedUpTo && setCurrentIndex(i)}
                className={`w-7 h-7 rounded-full text-xs font-medium transition-all ${
                  i >= unlockedUpTo ? 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed' :
                  i === currentIndex ? 'bg-purple-600 text-white' :
                  answers[q.id] !== undefined ? 'bg-emerald-500 text-white' :
                  'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                }`}
                title={i >= unlockedUpTo ? 'Play next media to unlock' : `Question ${i + 1}`}
              >
                {i >= unlockedUpTo ? '🔒' : i + 1}
              </button>
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (phase === 'result' && result) {
    const percentage = result.percentage || 0;
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
          <div className={`rounded-2xl p-8 text-center text-white ${
            percentage >= 80 ? 'bg-gradient-to-br from-emerald-500 to-teal-600' :
            percentage >= 50 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
            'bg-gradient-to-br from-rose-500 to-pink-600'
          }`}>
            <Trophy className="h-16 w-16 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2">{percentage}%</h2>
            <p className="text-xl mb-1">
              {percentage >= 80 ? 'Excellent!' : percentage >= 50 ? 'Good Job!' : 'Keep Practicing!'}
            </p>
            <p className="text-white/80">{result.correct_count} / {result.total_questions} correct</p>
          </div>

          <div className="space-y-3">
            {result.results?.map((r, i) => (
              <Card key={i} className={`border-l-4 ${r.is_correct ? 'border-l-emerald-500' : 'border-l-rose-500'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {r.is_correct
                      ? <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                      : <XCircle className="h-5 w-5 text-rose-500 mt-0.5 shrink-0" />
                    }
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 dark:text-white text-sm mb-1">
                        Q{i + 1}: {questions[i]?.question_text}
                      </p>
                      <p className={`text-xs ${r.is_correct ? 'text-emerald-600' : 'text-rose-600'}`}>
                        Your answer: {r.your_answer}
                      </p>
                      {!r.is_correct && (
                        <p className="text-xs text-emerald-600">Correct: {r.correct_answer}</p>
                      )}
                      {r.explanation && (
                        <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            <span className="font-semibold">Explanation: </span>{r.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" onClick={goBack} className="flex-1">Back to Tests</Button>
            <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={() => startTest(selectedTest)}>
              Try Again
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return null;
};

export default StudentVMCQPage;
